import { konfidencyAdapter } from './reviewsAdapterKonfidency';
import {
  mapKonfidencyReview,
  mapKonfidencyStoreSummary,
  extractReviewsArray,
  extractTotal,
} from './reviewsMapper';
import { getCached, setCached } from './reviewsCache';
import {
  HIGHLIGHT_FALLBACK_STRATEGY,
  HOME_CAROUSEL_MAX,
} from '@/data/reviewsHighlights';
import { getKonfidencyId, ALL_KONFIDENCY_SKUS, PRODUCTS_SKU_MAP } from '@/data/productsMapping';
import type { Review, ReviewsSummary, PaginatedReviews } from '@/types/reviews';

// ANVISA RDC 752/2022 — compliance blocklist for home carousel.
// Reviews matching any of these patterns contain prohibited therapeutic claims
// for cosmetics and must NOT be displayed on the homepage carousel.
// Note: reviews remain in the source data (Konfidency); only the homepage hides them.
const HOME_CAROUSEL_BLOCKED_PATTERNS: RegExp[] = [
  /cicatriza[çc]/i,         // CHANGE-01: "acelerou cicatrização" (Viviane Xixirry)
  /c(ã|a|â)i?mbra/i,         // CHANGE-02: "melhora nas cãibras" (Bruno)
];

function isBlockedForHomeCarousel(review: { body?: string; authorName?: string }): boolean {
  const text = `${review.body ?? ''} ${review.authorName ?? ''}`;
  return HOME_CAROUSEL_BLOCKED_PATTERNS.some(rx => rx.test(text));
}

async function safe<T>(label: string, fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn(`[ReviewsService] ✗ Failed: ${label}`, err);
    }
    return null;
  }
}

export const reviewsService = {
  async getHomeCarouselReviews(): Promise<Review[]> {
    const cacheKey = 'home:carousel';
    const cached = getCached<Review[]>(cacheKey);
    if (cached) return cached;

    // Fetch store carousel (few reviews)
    const storeRaw = await safe('getStoreCarousel', () => konfidencyAdapter.getStoreCarousel());
    let reviews: Review[] = storeRaw
      ? extractReviewsArray(storeRaw)
          .filter(r => String(r.text ?? '').trim().length > 0)
          .map(r => mapKonfidencyReview(r, 'store'))
      : [];

    // Supplement with product reviews from high-volume products
    const topProducts = [...PRODUCTS_SKU_MAP]
      .sort((a, b) => b.reviewCount - a.reviewCount)
      .slice(0, 5);

    const productFetches = topProducts.map(product =>
      safe(
        `carousel:${product.handle}`,
        () => konfidencyAdapter.getProductReviews(product.reviewsKonfidencyId, 1, 10, 'helpfulScore,desc')
      ).then(raw => {
        if (!raw) return [];
        return extractReviewsArray(raw)
          .filter(r => String(r.text ?? '').trim().length > 10)
          .map(r => ({
            ...mapKonfidencyReview(r, 'product'),
            productHandle: product.handle,
            productName: product.name,
            productId: product.shopifyId,
          }));
      })
    );

    const productResults = await Promise.all(productFetches);
    reviews = [...reviews, ...productResults.flat()];

    // Sort: highlighted first, then verified, then recent
    reviews.sort((a, b) => {
      if (a.isHighlighted !== b.isHighlighted) return a.isHighlighted ? -1 : 1;
      if (a.isHighlighted && b.isHighlighted) return a.displayPriority - b.displayPriority;
      if (a.verified !== b.verified) return a.verified ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    // Deduplicate by id
    const seen = new Set<string>();
    reviews = reviews.filter(r => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });

    // ANVISA compliance filter — drop reviews with prohibited therapeutic claims
    reviews = reviews.filter(r => !isBlockedForHomeCarousel(r));

    const result = reviews.slice(0, HOME_CAROUSEL_MAX);
    setCached(cacheKey, result, 10 * 60 * 1000);
    return result;
  },

  async getProductReviews(productHandle: string, page = 1, pageSize = 6): Promise<PaginatedReviews> {
    const cacheKey = `pdp:${productHandle}:${page}`;
    const cached = getCached<PaginatedReviews>(cacheKey);
    if (cached) return cached;

    const konfidencyId = getKonfidencyId(productHandle);
    let reviews: Review[] = [];
    let total = 0;

    if (konfidencyId) {
      const raw = await safe(
        `getProductReviews:${productHandle}`,
        () => konfidencyAdapter.getProductReviews(konfidencyId, page, pageSize)
      );
      if (raw) {
        reviews = extractReviewsArray(raw).map(r => mapKonfidencyReview(r, 'product'));
        total = extractTotal(raw, reviews.length);
        reviews = reviews.map(r => ({ ...r, productHandle: r.productHandle || productHandle }));
      }
    }

    if (reviews.length < 2) {
      const storeRaw = await safe('getStoreCarousel:fallback', () => konfidencyAdapter.getStoreCarousel());
      if (storeRaw) {
        const storeReviews = extractReviewsArray(storeRaw)
          .map(r => mapKonfidencyReview(r, 'store'))
          .filter(r => r.rating >= 4)
          .slice(0, pageSize - reviews.length);
        reviews = [...reviews, ...storeReviews];
        total = Math.max(total, reviews.length);
      }
    }

    const result: PaginatedReviews = {
      reviews: reviews.slice(0, pageSize),
      total,
      page,
      pageSize,
      hasMore: total > page * pageSize,
    };

    setCached(cacheKey, result, 3 * 60 * 1000);
    return result;
  },

  async getAllReviews(
    type: 'all' | 'store' | 'product' = 'all',
    page = 1,
    pageSize = 12,
    sort: 'recent' | 'relevant' = 'recent'
  ): Promise<PaginatedReviews> {
    const cacheKey = `hub:${type}:${sort}:${page}`;
    const cached = getCached<PaginatedReviews>(cacheKey);
    if (cached) return cached;

    const konfidencySort = sort === 'recent' ? 'created,desc' : 'helpfulScore,desc';
    let allReviews: Review[] = [];

    // Fetch store reviews
    if (type === 'store' || type === 'all') {
      const storeRaw = await safe(
        'getStoreReviews',
        () => konfidencyAdapter.getStoreReviews(konfidencySort, 1, 50)
      );
      if (storeRaw) {
        const storeItems = extractReviewsArray(storeRaw)
          .filter(r => String(r.text ?? '').trim().length > 0);
        allReviews = [...allReviews, ...storeItems.map(r => mapKonfidencyReview(r, 'store'))];
      }
    }

    // Fetch product reviews from all 10 products in parallel
    if (type === 'product' || type === 'all') {
      const productFetches = PRODUCTS_SKU_MAP.map(product =>
        safe(
          `getProductReviews:${product.handle}`,
          () => konfidencyAdapter.getProductReviews(product.reviewsKonfidencyId, 1, 50, konfidencySort)
        ).then(raw => {
          if (!raw) return [];
          return extractReviewsArray(raw).map(r => ({
            ...mapKonfidencyReview(r, 'product'),
            productHandle: product.handle,
            productName: product.name,
            productId: product.shopifyId,
          }));
        })
      );

      const productResults = await Promise.all(productFetches);
      allReviews = [...allReviews, ...productResults.flat()];
    }

    // Sort combined results
    allReviews.sort((a, b) => {
      if (sort === 'recent') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return b.rating - a.rating;
    });

    const total = allReviews.length;
    const start = (page - 1) * pageSize;
    const pageReviews = allReviews.slice(start, start + pageSize);

    const result: PaginatedReviews = {
      reviews: pageReviews,
      total,
      page,
      pageSize,
      hasMore: total > page * pageSize,
    };

    setCached(cacheKey, result, 5 * 60 * 1000);
    return result;
  },

  async getReviewsSummary(): Promise<ReviewsSummary | null> {
    const cacheKey = 'store:summary';
    const cached = getCached<ReviewsSummary>(cacheKey);
    if (cached) return cached;

    const raw = await safe('getStoreSummary', () => konfidencyAdapter.getStoreSummary());

    const productReviewTotal = PRODUCTS_SKU_MAP.reduce((sum, p) => sum + p.reviewCount, 0);

    let storeCount = 2;
    let storeAvg = 5;

    if (raw) {
      const obj = Array.isArray(raw) ? (raw[0] as Record<string, unknown> ?? {}) : (raw as Record<string, unknown>);
      storeCount = Number(obj.count ?? 2);
      storeAvg = Number(obj.avgRating ?? 5);
    }

    const totalReviews = productReviewTotal + storeCount;

    const summary: ReviewsSummary = {
      averageRating: storeAvg,
      totalReviews,
      distribution: { 5: totalReviews, 4: 0, 3: 0, 2: 0, 1: 0 },
    };

    if (summary.totalReviews === 0) return null;

    setCached(cacheKey, summary, 15 * 60 * 1000);
    return summary;
  },

  async getAllProductRatings(): Promise<Record<string, { avg: number; count: number }>> {
    const cacheKey = 'plp:all_ratings';
    const cached = getCached<Record<string, { avg: number; count: number }>>(cacheKey);
    if (cached) return cached;

    const raw = await safe('getProductRatings', () => konfidencyAdapter.getProductRatings(ALL_KONFIDENCY_SKUS));
    if (!raw || typeof raw !== 'object') return {};

    const result: Record<string, { avg: number; count: number }> = {};
    const data = raw as Record<string, unknown>;
    for (const sku of ALL_KONFIDENCY_SKUS) {
      const entry = data[sku] as Record<string, unknown> | undefined;
      if (entry) {
        result[sku] = {
          avg: Number(entry.average ?? entry.avg ?? entry.media ?? entry.rating ?? 0),
          count: Number(entry.total ?? entry.count ?? entry.totalCount ?? 0),
        };
      }
    }

    setCached(cacheKey, result, 10 * 60 * 1000);
    return result;
  },
};
